import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const THEMES = {
  blue: {
    name: 'Ocean Blue',
    primary: '#1e40af',
    primaryLight: '#3b82f6',
    primaryDark: '#1e3a8a',
    accent: '#0ea5e9',
    gradient: 'linear-gradient(135deg, #1e40af 0%, #0ea5e9 100%)',
  },
  green: {
    name: 'Forest Green',
    primary: '#166534',
    primaryLight: '#22c55e',
    primaryDark: '#14532d',
    accent: '#10b981',
    gradient: 'linear-gradient(135deg, #166534 0%, #10b981 100%)',
  },
  purple: {
    name: 'Royal Purple',
    primary: '#6b21a8',
    primaryLight: '#a855f7',
    primaryDark: '#581c87',
    accent: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #6b21a8 0%, #8b5cf6 100%)',
  },
  orange: {
    name: 'Sunset Orange',
    primary: '#c2410c',
    primaryLight: '#f97316',
    primaryDark: '#9a3412',
    accent: '#fb923c',
    gradient: 'linear-gradient(135deg, #c2410c 0%, #fb923c 100%)',
  },
  teal: {
    name: 'Teal',
    primary: '#0f766e',
    primaryLight: '#14b8a6',
    primaryDark: '#134e4a',
    accent: '#2dd4bf',
    gradient: 'linear-gradient(135deg, #0f766e 0%, #2dd4bf 100%)',
  },
  slate: {
    name: 'Slate Grey',
    primary: '#334155',
    primaryLight: '#64748b',
    primaryDark: '#1e293b',
    accent: '#94a3b8',
    gradient: 'linear-gradient(135deg, #334155 0%, #64748b 100%)',
  },
};

export const ThemeProvider = ({ children }) => {
  const [themeKey, setThemeKey] = useState(() => {
    return localStorage.getItem('auxtax_theme') || 'blue';
  });
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('auxtax_dark') === 'true';
  });

  const theme = THEMES[themeKey] || THEMES.blue;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.primary);
    root.style.setProperty('--color-primary-light', theme.primaryLight);
    root.style.setProperty('--color-primary-dark', theme.primaryDark);
    root.style.setProperty('--color-accent', theme.accent);
    root.style.setProperty('--gradient-primary', theme.gradient);

    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme, darkMode]);

  const changeTheme = (key) => {
    if (THEMES[key]) {
      setThemeKey(key);
      localStorage.setItem('auxtax_theme', key);
    }
  };

  const toggleDarkMode = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('auxtax_dark', String(next));
  };

  return (
    <ThemeContext.Provider value={{ theme, themeKey, darkMode, changeTheme, toggleDarkMode, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export default ThemeContext;